import * as React from 'react';

import {Routing} from 'nutshell-core/routing';
import type {User} from 'nutshell-core/types';

import {BulletedList} from 'shells/bulleted-list/bulleted-list';
import {Button} from 'shells/button/button';
import {Illustration} from 'shells/illustrations/illustration';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Link} from 'shells/link/link';
import {TextField} from 'shells/textfield/textfield';
import {Body} from 'shells/typography/body';

import {useGetInstanceInfo} from '../../../core/use-get-instance-info';

import {analyticsClient} from '../../../../analytics-client';

import {useGetGuessedInstanceWebsite} from './graphql/hooks/use-get-guessed-instance-website';

import styles from './seo-scan-page-content.module.css';

const HIGHLIGHTS_BULLETS = [
    <Body key='1'>
        <b>Your SEO score:</b> Understand the overall health of your site at a glance.
    </Body>,
    <Body key='2'>
        <b>Identify issues:</b> Pinpoint critical issues that are keeping your site from being seen.
    </Body>,
    <Body key='3'>
        <b>Actionable fixes:</b> Get clear, actionable steps to improve your site’s performance on
        Google and Bing.
    </Body>,
    <Body key='4'>
        <b>Prioritization guide:</b> Discover which fixes will have the biggest impact on your
        search rankings.
    </Body>,
    <Body key='5'>
        <b>Learn more:</b> Access tailored resources to deepen your understanding of your specific
        marketing challenges.
    </Body>,
];

type Props = {
    user: User | null | undefined;
};

export function SEOScanPageContent(props: Props) {
    const [url, setUrl] = React.useState<string>('');

    const {sessionInstance} = useGetInstanceInfo();
    const {guessedInstanceWebsite} = useGetGuessedInstanceWebsite();

    React.useEffect(() => {
        if (guessedInstanceWebsite) {
            setUrl(guessedInstanceWebsite);
        }
    }, [guessedInstanceWebsite]);

    const handleSubmitScan = () => {
        analyticsClient.track(analyticsClient.EVENTS.TRACK_SEO_SCAN_SUBMITTED, {
            url: url,
        });

        const params = {
            url: url,
            direct: true,
            source: 'nutshell_app',
            company: sessionInstance ? sessionInstance.companyName : '',
            userEmail: props.user && props.user.emails ? props.user.emails[0] : '',
        } as const;
        const queryString = Routing.param(params);
        window.open(`https://www.webfx.com/seo-checker/?${queryString}`, '_blank');
    };

    return (
        <StackLegacy spacing={48}>
            <Body>
                Get a free website analysis powered by{' '}
                <Link variant='primary' isBold={true} href={`https://www.webfx.com/`}>
                    WebFX
                </Link>
                , our trusted digital marketing partner. This audit will help you understand how to
                attract more web visitors and convert them into leads.
            </Body>
            <StackLegacy spacing={48} isHorizontal={true}>
                <div className={styles['scan-input-container']}>
                    <StackLegacy spacing={12}>
                        <Illustration
                            src='start-here'
                            width={100}
                            fullPath='/include/images/start-here.png'
                        />
                        <TextField placeholder='Your website URL' value={url} onChange={setUrl} />
                        <Button
                            variant='primary'
                            size='xlarge'
                            isFullWidth={true}
                            onClick={handleSubmitScan}
                            disabled={!url}
                        >
                            Let’s do this
                        </Button>
                    </StackLegacy>
                </div>
                <Illustration src='man-magnifying-crowd' width={172} />
            </StackLegacy>
            <StackLegacy spacing={24}>
                <Body size='large' isBold={true}>
                    Highlights
                </Body>
                <BulletedList bullets={HIGHLIGHTS_BULLETS} />
            </StackLegacy>
        </StackLegacy>
    );
}
